/**
 * Mijn Verlof Page (Spoor 2, Verlof MVP — Story 1.2, UX V1)
 *
 * Eigen verlofoverzicht: vier saldo-kaarten + historie met status-badges.
 * Strikt eigen-gegevens (AD-4) — deze pagina neemt of verzendt nooit een
 * employeeId; het backend-endpoint (`/leave-requests/me`) leidt dat zelf af
 * uit de ingelogde gebruiker.
 */

import { Fragment, useState } from 'react';
import {
  soortBoektNooit,
  vandaagInAmsterdam,
  type OwnLeaveHistoryEntry,
  type PeriodeAanpassing,
} from '@functieloonhuis/shared';
import { Layout } from '../../components/Layout';
import { useAuth } from '../auth';
import { useMijnVerlof } from './hooks/useMijnVerlof';
import { useWithdrawLeaveRequest } from './hooks/useWithdrawLeaveRequest';
import { useConfirmLeaveHours } from './hooks/useConfirmLeaveHours';
import { LeaveRequestSheet } from './LeaveRequestSheet';
import { LeaveCorrectionSheet } from './LeaveCorrectionSheet';
import { UrenBevestigenKaart } from './UrenBevestigenKaart';
import { StatusBadge } from './StatusBadge';
import {
  AfgerondDoorHrRegel,
  GeenSoortRegel,
  HrStapStatusregel,
  UrenwijzigingIndienerRegel,
} from './HrStapStatus';
import {
  MARKERING_EINDDATUM_VOORLOPIG,
  ZIN_VOOR_MEDEWERKER,
} from './bijzonderVerlofTeksten';
import { useVerlofsoortIngangsdatum } from './hooks/useVerlofsoortIngangsdatum';
import { SyncStatusChip } from './SyncStatusChip';
import { LeaveHistoryList } from './LeaveHistoryList';
import { slotregelTekst } from './correctieTermijnCopy';
import {
  formatSaldoGetal,
  formatSaldoMetDagen,
  formatJaarruimte,
  formatJaarruimteToelichting,
} from '../../lib/verlofSaldo';

/**
 * AD-16/B10: los van de termijn -- uit dienst blokkeert niet-HR volledig,
 * ongeacht hoe lang geleden het verlof was. Deze pagina is strikt eigen-scope
 * (AD-4, zie header), dus deze regel gaat altijd over de ingelogde gebruiker
 * zelf -- eerste persoon ("Je bent"), niet "deze medewerker" (dat las als
 * een gesprek over een derde in wat een persoonlijk overzicht is).
 */
const UIT_DIENST_REDEN = 'Je bent uit dienst — alleen HR kan dit nog aanpassen.';

/** Story 7.7, AC-6 (§10.5, besluit V29) — letterlijk, en nergens herschreven. */
const MELDING_GOEDKEURING_TERUGGENOMEN =
  'Je leidinggevende heeft zijn goedkeuring teruggenomen. De aanvraag wacht weer op zijn beoordeling.';

/**
 * Story 3.2, taak 4 (besluit van de opdrachtgever, 2026-08-12) — de regel die
 * bij *bevestigd* én bij *buiten bereik* verklaart waarom de knop *Uren
 * aanpassen* weg is. Letterlijk uit de microcopy-tabel (UX-DR14).
 *
 * Eén zin voor beide toestanden, met opzet: het verschil ertussen verandert
 * niets aan wat de medewerker nog kan.
 */
const VASTE_UREN_REDEN = 'Deze uren staan vast.';

/** Het id van de verklarende regel, zodat de verlofrij ernaar kan verwijzen (AC-10). */
function definitiefRegelId(id: number): string {
  return `leave-history-definitief-note-${id}`;
}

const MAANDEN_VOLUIT = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
];

/**
 * "3 t/m 7 augustus" — de vorm waarin §10.5 een periode schrijft: de maand
 * staat er één keer, achteraan. Loopt de periode over een maandgrens, dan
 * krijgt de begindatum zijn eigen maand, anders is de zin niet te lezen.
 *
 * De datums komen als kale `YYYY-MM-DD` binnen: geen tijdzone in het spel,
 * dus hier ook geen `new Date()` die er per ongeluk een dag af haalt.
 */
function periodeInWoorden(vanaf: string, tot: string): string {
  const [, vanMaand, vanDag] = vanaf.split('-');
  const [, totMaand, totDag] = tot.split('-');
  if (!vanMaand || !vanDag || !totMaand || !totDag) return `${vanaf} t/m ${tot}`;
  const totNaam = MAANDEN_VOLUIT[Number(totMaand) - 1] ?? '';
  if (vanMaand === totMaand) return `${Number(vanDag)} t/m ${Number(totDag)} ${totNaam}`;
  const vanNaam = MAANDEN_VOLUIT[Number(vanMaand) - 1] ?? '';
  return `${Number(vanDag)} ${vanNaam} t/m ${Number(totDag)} ${totNaam}`;
}

/**
 * Story 7.8, AC-11 (§10.5, besluit V38) — de vastgestelde zin, met alleen de
 * twee perioden ingevuld. Géén reden: die staat in de historie.
 */
function periodeAanpassingMelding(aanpassing: PeriodeAanpassing): string {
  const oud = periodeInWoorden(aanpassing.oudeStartDatum, aanpassing.oudeEindDatum);
  const nieuw = periodeInWoorden(aanpassing.nieuweStartDatum, aanpassing.nieuweEindDatum);
  return `HR heeft de periode van je verlof aangepast: van ${oud} naar ${nieuw}.`;
}

/**
 * Story 2.5: rendert de "Uren aanpassen"-actie voor een goedgekeurde
 * verlofregel, of de reden waarom die ontbreekt (AC-4/AC-5/AC-7/AC-8).
 * Als eigen component i.p.v. een IIFE in de tabel-JSX, zodat de per-rij
 * afleiding een gewone functie-body is; `peildatum` komt van de pagina
 * (één keer per render bepaald) zodat elke rij niet zelf opnieuw
 * `vandaagInAmsterdam()` hoeft aan te roepen.
 */
function CorrectionAction({
  entry,
  isHrOrAdmin,
  isEmployeeUitDienst,
  peildatum,
  onCorrect,
}: {
  entry: OwnLeaveHistoryEntry;
  isHrOrAdmin: boolean;
  isEmployeeUitDienst: boolean;
  peildatum: string;
  onCorrect: (entry: OwnLeaveHistoryEntry) => void;
}) {
  // Story 2.5: uit dienst (AD-16/B10) blokkeert volledig voor iedereen
  // buiten HR/admin, ongeacht de termijn -- die check gaat daarom vóór de
  // termijn-check.
  const termijnVerstreken = entry.correctieTermijnVerstreken === true;
  const toonKnop = isHrOrAdmin || (!isEmployeeUitDienst && !termijnVerstreken);

  if (!toonKnop) {
    if (isEmployeeUitDienst) {
      return (
        <p
          data-testid={`leave-history-reden-${entry.id}`}
          role="note"
          className="text-xs text-gray-500"
        >
          {UIT_DIENST_REDEN}
        </p>
      );
    }

    // Story 3.2, taak 4 — DEZELFDE regel als sinds story 2.5, maar nu
    // aangestuurd door de afgeleide toestand in plaats van door de kale
    // termijn. Zonder die wissel verscheen "uren definitief geworden op …"
    // óók bij verlof dat wél bevestigd is (daar zijn de uren niet
    // stilzwijgend definitief geworden) en bij verlof buiten het bereik
    // (daar is nooit iets gevraagd).
    if (entry.bevestigingsToestand === 'niet_bevestigd_definitief' && entry.definitiefGewordenOp) {
      return (
        <p
          id={definitiefRegelId(entry.id)}
          data-testid={`leave-history-definitief-${entry.id}`}
          role="note"
          className="text-xs text-gray-500"
        >
          {slotregelTekst(entry.definitiefGewordenOp, true, peildatum)}
        </p>
      );
    }

    if (entry.bevestigingsToestand === 'bevestigd' || entry.bevestigingsToestand === 'buiten_bereik') {
      return (
        <p
          data-testid={`leave-history-reden-${entry.id}`}
          role="note"
          className="text-xs text-gray-500"
        >
          {VASTE_UREN_REDEN}
        </p>
      );
    }

    // Terugval voor regels die de toestand (nog) niet dragen — bestaand
    // gedrag uit story 2.5, ongewijzigd.
    return (
      <p
        data-testid={`leave-history-reden-${entry.id}`}
        role="note"
        className="text-xs text-gray-500"
      >
        {entry.correctieTermijnEinde
          ? slotregelTekst(entry.correctieTermijnEinde, true, peildatum)
          : null}
      </p>
    );
  }

  return (
    <div className="text-right">
      <button
        type="button"
        onClick={() => onCorrect(entry)}
        data-testid={`leave-history-correct-${entry.id}`}
        className="text-xs text-[#54949E] hover:underline"
      >
        Uren aanpassen
      </button>
      {/* AC-7: alleen zinvol zolang de termijn nog loopt -- ná afloop toont
          HR (die de knop nog wél ziet) de "definitief geworden"-regel niet
          nogmaals, want dat is al de reden waarom een niet-HR-gebruiker de
          knop kwijt is; voor HR blijft de knop bruikbaar. */}
      {!isEmployeeUitDienst && !termijnVerstreken && entry.correctieTermijnEinde && (
        <p
          data-testid={`leave-history-termijn-${entry.id}`}
          className="text-xs text-gray-400 mt-0.5"
        >
          {slotregelTekst(entry.correctieTermijnEinde, false, peildatum)}
        </p>
      )}
    </div>
  );
}

export function MijnVerlofPage() {
  const { data, isLoading } = useMijnVerlof();
  const overview = data?.data;
  const { user, hasRole } = useAuth();
  // Story 2.5 (AC-4/AC-5): HR/admin mag de ingang altijd nog zien; iedere
  // andere rol verliest hem zodra de termijn verstreken is of de medewerker
  // uit dienst is (AD-16/B10) -- `isTeamManagerOrHR` is hier te breed, want
  // die telt ook een gewone leidinggevende mee.
  const isHrOrAdmin = hasRole(['hr', 'admin']);
  // Story 2.5 (AC-7): één keer per render bepaald en hergebruikt voor elke
  // rij, in plaats van dat elke rij zelf `vandaagInAmsterdam()` aanroept.
  const peildatum = vandaagInAmsterdam();
  // Story 5.4 (AC-7): de schakelaar. Zolang hij niet gezet is zwijgt
  // `GeenSoortRegel` en ziet deze pagina er precies zo uit als vandaag.
  const ingangsdatum = useVerlofsoortIngangsdatum().data ?? null;
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  // Story 2.4 (Taak 9): geen nieuwe knop "Nee, aanpassen" (die hoort bij
  // story 3.1) -- deze story opent de correctiesheet vanaf de bestaande
  // verlofregel, zodat epic 2 ook zonder epic 3 bruikbaar is.
  const [correctingEntry, setCorrectingEntry] = useState<OwnLeaveHistoryEntry | null>(null);
  // Story 2.10 (FR-16): welke verlofregels hun historie uitgeklapt hebben. Een
  // verzameling en geen enkel id, zodat twee regels tegelijk open kunnen staan
  // — vergelijken is precies waarvoor iemand de historie opent.
  const [openHistorie, setOpenHistorie] = useState<Set<number>>(new Set());
  const withdraw = useWithdrawLeaveRequest();
  // Story 3.1 (AC-3/AC-9): bevestigen is één klik. De regel verdwijnt pas als
  // het VERSE overzicht hem niet meer draagt -- de pagina haalt hem nooit zelf
  // weg, want zolang het antwoord van de server niet binnen is weet ze niet of
  // de bevestiging is aangenomen.
  const confirmHours = useConfirmLeaveHours();
  const [zojuistBevestigd, setZojuistBevestigd] = useState<number[]>([]);

  function handleBevestig(id: number) {
    confirmHours.mutate(id);
    setZojuistBevestigd((huidig) => (huidig.includes(id) ? huidig : [...huidig, id]));
  }

  /**
   * Story 3.1, AC-5: "Nee, aanpassen" opent de BESTAANDE correctie-sheet uit
   * story 2.4, voorgevuld met dit verlof. Geen tweede formulier in de kaart.
   */
  function handleAanpassen(id: number) {
    const entry = (overview?.history ?? []).find((regel) => regel.id === id);
    if (entry) setCorrectingEntry(entry);
  }

  function toggleHistorie(id: number) {
    setOpenHistorie((huidig) => {
      const volgende = new Set(huidig);
      if (volgende.has(id)) volgende.delete(id);
      else volgende.add(id);
      return volgende;
    });
  }

  function handleWithdraw(id: number) {
    // Bevestigingsdialoog voor een destructieve actie, consistent met het
    // bestaande window.confirm-patroon elders (bv. RolesTab.tsx, Step6Agreements.tsx).
    if (window.confirm('Weet je zeker dat je deze verlofaanvraag wilt intrekken?')) {
      withdraw.mutate(id);
    }
  }

  return (
    <Layout>
      <div data-testid="mijn-verlof-page" className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1E293B]">Mijn verlof</h1>
            <p className="mt-1 text-sm text-gray-500">
              Je verlofsaldo, historie en status van je aanvragen.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsSheetOpen(true)}
            disabled={isLoading || overview?.linked === false}
            className="px-4 py-2 rounded-md bg-[#2F5A7A] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            + Verlof aanvragen
          </button>
        </div>

        <LeaveRequestSheet isOpen={isSheetOpen} onClose={() => setIsSheetOpen(false)} />

        {correctingEntry && (
          <LeaveCorrectionSheet
            isOpen={true}
            onClose={() => setCorrectingEntry(null)}
            historyEntry={correctingEntry}
            employeeName={user?.name}
            employeeUitDienstDatum={overview?.employeeUitDienstDatum ?? null}
          />
        )}

        {/* Story 3.1, AC-1/AC-14: de herinneringskaart staat tussen de kop en
            de saldokaarten, en uitsluitend hier -- geen teller in het menu,
            geen widget op het startscherm (B19, FR-13). Welke regels erin
            staan bepaalt de server; is de lijst leeg, dan rendert de kaart
            zichzelf weg (AC-11). Zonder loonkoppeling blijft hij helemaal
            weg: dan is er niets te bevestigen dat ergens terechtkomt. */}
        {!isLoading && overview?.linked !== false && (
          <UrenBevestigenKaart
            regels={overview?.teBevestigen ?? []}
            peildatum={peildatum}
            onBevestig={handleBevestig}
            onAanpassen={handleAanpassen}
            zojuistBevestigd={zojuistBevestigd}
          />
        )}

        {isLoading ? (
          <div className="bg-white p-6 rounded-lg border border-[#E2E8F0] text-sm text-gray-500">
            Laden...
          </div>
        ) : overview?.linked === false ? (
          <div className="bg-white p-6 rounded-lg border border-[#E2E8F0] text-sm text-gray-500 mb-6">
            Verlofsaldo niet beschikbaar — geen loonkoppeling
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {(overview?.balances ?? []).map((balance) => (
              <div
                key={balance.type}
                data-testid="balance-card"
                className="bg-white p-4 rounded-lg border border-[#E2E8F0]"
              >
                <div className="text-sm text-gray-500">{balance.type}</div>

                {balance.yearRemaining !== null ? (
                  <>
                    {/* De vraag die iemand écht stelt is "kan ik in juli drie
                        weken weg?". Daarom staat de jaarruimte voorop en de
                        opgebouwde stand eronder als onderbouwing — andersom
                        leest een laag februari-getal als een weigering. */}
                    <div className="text-xs text-gray-500 mt-1">Nog te plannen dit jaar</div>
                    <div
                      className="text-2xl font-bold text-[#2F5A7A] leading-tight"
                      aria-label={`${formatJaarruimte(
                        balance.yearRemaining,
                        balance.hoursPerDay,
                        balance.varyingSchedule
                      )}, ${balance.yearRemaining} uur. Verwachting als je het jaar uitwerkt.`}
                    >
                      {formatJaarruimte(
                        balance.yearRemaining,
                        balance.hoursPerDay,
                        balance.varyingSchedule
                      )}
                    </div>
                    <div className="text-xs text-gray-400">
                      {formatJaarruimteToelichting(
                        balance.yearRemaining,
                        balance.entitlementBasisDays
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-[#E2E8F0]">
                      Nu opgebouwd: {formatSaldoMetDagen(balance.remaining, balance.hoursPerDay)}
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className={`text-2xl font-bold mt-1 ${
                        balance.remaining === null ? 'text-gray-400 italic' : 'text-[#1E293B]'
                      }`}
                    >
                      {formatSaldoGetal(balance.remaining)}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {balance.remaining === null
                        ? 'saldo nu niet op te halen'
                        : 'uur opgebouwd — stand van vandaag'}
                    </div>
                  </>
                )}
                {/* Story 1.5: uitsluitend Nmbrs' eigen al-berekende Expired-cijfer
                    tonen -- geen eigen wettelijke-vervalkalenderlogica, geen
                    countdown. Bij expired=0: geen extra regel (AC 5). */}
                {balance.expired > 0 && (
                  <div
                    className={`text-xs text-[#54949E] ${
                      // De jaarruimte-variant heeft al een scheidingslijn boven
                      // "Nu opgebouwd"; nog een lijn geeft een dubbele rand.
                      balance.yearRemaining !== null
                        ? 'mt-1'
                        : 'mt-2 border-t border-[#E2E8F0] pt-2'
                    }`}
                  >
                    Vervallen dit boekjaar: {balance.expired} uur
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-2">Periode</th>
                <th className="px-4 py-2">Verloftype</th>
                <th className="px-4 py-2">Uren</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Boeking</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(overview?.history ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                    Nog geen aanvragen — gebruik "+ Verlof aanvragen" om te starten.
                  </td>
                </tr>
              ) : (
                overview!.history.map((entry) => {
                  const isHistorieOpen = openHistorie.has(entry.id);
                  // AC-8/NFR-2: kleur is nooit de enige drager. Bij vervallen
                  // verlof staan de uren doorgehaald én draagt de badge het
                  // woord "Vervallen" — de doorhaling alleen is niet genoeg.
                  // `displayStatus` komt uit `deriveLeaveDisplayStatus`, die zelf al
                  // vroegtijdig `null` teruggeeft voor niet-goedgekeurd verlof (AC-4) --
                  // dus één bron van waarheid volstaat. Geen losse `hours === 0`-check
                  // erbij: die maakte een 0-uur-aanvraag die nog in afwachting is (bv.
                  // buiten het rooster ingediend, story 1.3) ten onrechte doorgehaald
                  // (CR/NFR-bevinding, story 2.10).
                  const isVervallen = entry.displayStatus === 'Vervallen';
                  // Story 3.2, AC-12 (besluit V26): bij een verlofsoort die
                  // nooit in de loonadministratie belandt blijft de badge
                  // *Niet bevestigd* weg. Er wordt bij die soort nooit om een
                  // bevestiging gevraagd, dus zou de badge een verzuim
                  // aanwrijven dat niet bestaat. De verklarende regel "uren
                  // definitief geworden op …" blijft er wél staan. De toets
                  // leest de gedeelde lijst (AD-27) en nooit een soortnaam of
                  // een boekingstoestand (KE-9, KE-10).
                  // Story 3.1, AC-5: staat dit verlof al in de herinneringskaart
                  // bovenaan, dan biedt die kaart de weg naar de correctie-sheet
                  // al met *Nee, aanpassen* — en met dezelfde termijnregel
                  // eronder. Dezelfde handeling twee keer op één scherm aanbieden
                  // maakt de pagina onrustig zonder iets toe te voegen: de
                  // verlofregel laat zijn eigen ingang daarom weg zolang de kaart
                  // hem draagt. Verdwijnt de regel uit de kaart (bevestigd, of
                  // termijn verstreken), dan staat de ingang er weer, precies
                  // zoals story 2.5 hem neerzette.
                  const staatInKaart = (overview?.teBevestigen ?? []).some(
                    (kaartRegel) => kaartRegel.id === entry.id
                  );
                  const badgeVerborgenOmSoort =
                    entry.displayStatus === 'Niet bevestigd' && soortBoektNooit(entry.verlofsoort);
                  // AC-10: de verklarende regel hoort hoorbaar bij de rij waar
                  // hij over gaat, dus verwijst de rij ernaar. Dezelfde
                  // voorwaarde als `CorrectionAction` hierboven gebruikt.
                  const toonDefinitiefRegel =
                    entry.requestStatus === 'goedgekeurd' &&
                    !isHrOrAdmin &&
                    overview?.employeeUitDienstDatum == null &&
                    entry.correctieTermijnVerstreken === true &&
                    entry.bevestigingsToestand === 'niet_bevestigd_definitief' &&
                    entry.definitiefGewordenOp != null;
                  return (
                    <Fragment key={entry.id}>
                      <tr
                        className="border-t border-[#E2E8F0]"
                        aria-describedby={
                          toonDefinitiefRegel ? definitiefRegelId(entry.id) : undefined
                        }
                      >
                        <td className="px-4 py-2">
                          {/* AC-8: een échte knop met `aria-expanded`, dus met Tab
                          bereikbaar en met Enter/Space te bedienen — geen
                          klikbare `<td>` of `<div>`. */}
                          <button
                            type="button"
                            onClick={() => toggleHistorie(entry.id)}
                            aria-expanded={isHistorieOpen}
                            data-testid={`leave-history-toggle-${entry.id}`}
                            className="text-left hover:underline text-[#1E293B]"
                          >
                            <span aria-hidden="true" className="mr-1 text-gray-400">
                              {isHistorieOpen ? '▾' : '▸'}
                            </span>
                            {entry.startDate} — {entry.endDate}
                            <span className="sr-only"> — historie tonen</span>
                          </button>
                        </td>
                        <td className="px-4 py-2">{entry.leaveTypeName}</td>
                        <td
                          className={`px-4 py-2 ${isVervallen ? 'line-through text-gray-400' : ''}`}
                        >
                          {entry.hours}
                        </td>
                        <td className="px-4 py-2">
                          <StatusBadge
                            status={entry.requestStatus}
                            hours={entry.hours}
                            displayStatus={badgeVerborgenOmSoort ? null : (entry.displayStatus ?? null)}
                          />
                          {/* Story 7.2, AC-6: zolang HR nog moet kijken staat
                              hier in woorden wat er is gebeurd en wat er nog
                              komt. De regel rendert zichzelf weg zodra de
                              aanvraag ergens anders staat. */}
                          <HrStapStatusregel rij={entry} id={entry.id} />
                          {/* Story 8.1, AC-7 (§10.8): nadat HR heeft
                              goedgekeurd staat hier *Goedgekeurd door HR*. Bij
                              verlof zonder HR-stap blijft het kale *Goedgekeurd*
                              van de badge staan (KE-6). */}
                          <AfgerondDoorHrRegel rij={entry} />
                          {/* Story 11.1/11.2, AC-10/AC-9: ligt er een
                              urenwijziging bij HR, dan leest de indiener hier
                              wat er met zijn wijziging gebeurt. De drie
                              vastgestelde zinnen wonen in `HrStapStatus`; welke
                              van de drie erbij hoort volgt uit de route van de
                              WIJZIGING en het moment, niet uit de soort van de
                              aanvraag. */}
                          <UrenwijzigingIndienerRegel
                            correctieHrStapReden={entry.correctieHrStapReden}
                            correctieDecidedAt={entry.correctieDecidedAt}
                          />
                          {/* Story 10.4, AC-4 (§10.4): de begrafenisdatum stond
                              bij het indienen nog niet vast. TEKST en geen
                              kleurvlak (KE-3), en geen waarschuwingsvorm — er is
                              niets misgegaan. De markering verdwijnt vanzelf
                              zodra HR de einddatum vaststelt: dan komt de vlag
                              als onwaar terug van de server (AC-6). */}
                          {entry.einddatumVoorlopig === true && (
                            <>
                              <p
                                data-testid={`einddatum-voorlopig-${entry.id}`}
                                className="mt-1 text-xs font-medium text-[#2F5A7A]"
                              >
                                {MARKERING_EINDDATUM_VOORLOPIG}
                              </p>
                              <p className="mt-1 text-xs text-gray-600">{ZIN_VOOR_MEDEWERKER}</p>
                            </>
                          )}
                          {/* Story 5.4, AC-7: bij een aanvraag van vóór de
                              nieuwe regels staat hier de zin uit §10.7 — en
                              verder geen veld, geen label en geen streepje. */}
                          <GeenSoortRegel rij={entry} ingangsdatum={ingangsdatum} />
                          {/* Story 7.7, AC-6 (§10.5, besluit V29): de aanvraag
                              ligt weer bij de leidinggevende, en dat is geen
                              storing — dus geen `role="alert"` en geen
                              foutkleur. */}
                          {entry.goedkeuringTeruggenomen === true && (
                            <p
                              data-testid={`terugname-regel-${entry.id}`}
                              className="mt-1 text-xs text-gray-600"
                            >
                              {MELDING_GOEDKEURING_TERUGGENOMEN}
                            </p>
                          )}
                          {/* Story 7.8, AC-11 (§10.5, besluit V38): één melding
                              per aanpassing, zónder reden — die staat in de
                              historie, en bij een overlijden is het een
                              gevoelige zin om automatisch te tonen. */}
                          {(entry.periodeAanpassingen ?? []).map((aanpassing, index) => (
                            <p
                              key={`${aanpassing.oudeEindDatum}-${aanpassing.nieuweEindDatum}-${index}`}
                              data-testid={`periode-aanpassing-${entry.id}-${index}`}
                              className="mt-1 text-xs text-gray-600"
                            >
                              {periodeAanpassingMelding(aanpassing)}
                            </p>
                          ))}
                        </td>
                        <td className="px-4 py-2">
                          {/* Story 3.3, UX V5: sync-chip los van de besluit-badge; alleen
                          zinvol te tonen als er überhaupt een besluit is. Read-only
                          hier -- retry-actie hoort bij de leidinggevende/HR-kant
                          (ApprovalQueuePage), niet bij de medewerker zelf. */}
                          {entry.requestStatus === 'goedgekeurd' && (
                            <SyncStatusChip syncStatus={entry.syncStatus} />
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {entry.requestStatus === 'in_afwachting' && (
                            <button
                              type="button"
                              onClick={() => handleWithdraw(entry.id)}
                              disabled={withdraw.isPending}
                              className="text-xs text-[#54949E] hover:underline disabled:opacity-40"
                            >
                              Intrekken
                            </button>
                          )}
                          {entry.requestStatus === 'goedgekeurd' && !staatInKaart && (
                            <CorrectionAction
                              entry={entry}
                              isHrOrAdmin={isHrOrAdmin}
                              isEmployeeUitDienst={overview?.employeeUitDienstDatum != null}
                              peildatum={peildatum}
                              onCorrect={setCorrectingEntry}
                            />
                          )}
                        </td>
                      </tr>
                      {/* Voorwaardelijk gemonteerd: alleen zo blijft de bestaande
                      MijnVerlofPage.test.tsx (die useLeaveHistory niet mockt) intact --
                      een altijd-gemonteerde rij roept die hook ook aan wanneer niets is
                      uitgeklapt. Bewuste keuze om GEEN `aria-controls` te voeren (in
                      plaats van een rij die soms een dood element aanwijst): bij een
                      disclosure-widget waarvan de inhoud niet permanent bestaat, is
                      `aria-expanded` op de eigen knop voldoende (WAI-ARIA APG), zonder de
                      regressierisico's van een altijd-gemonteerde variant (CR/NFR-afweging,
                      story 2.10). */}
                      {isHistorieOpen && (
                        <tr className="border-t border-[#E2E8F0]">
                          <td
                            colSpan={6}
                            className="px-4 py-2"
                            id={`leave-history-panel-${entry.id}`}
                          >
                            <LeaveHistoryList
                              requestId={entry.id}
                              isOpen={isHistorieOpen}
                              naam={`Historie van verlof van ${entry.startDate} tot ${entry.endDate}`}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
